import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser-native Text-to-Speech hook (no API key, no backend round-trip).
 *
 * Uses the W3C SpeechSynthesis API — works on every modern browser, picks
 * up the user's own installed voices, and adapts to system locale (the
 * "responsive" part of the Architect's TTS directive).
 *
 * Returns:
 *   supported  — boolean, true if the browser exposes SpeechSynthesis
 *   speaking   — true while audio is playing
 *   speak(txt) — start (cancels anything already speaking first)
 *   stop()     — abort current utterance
 *   voices     — available voices for an optional picker UI
 */
export default function useSpeechSynthesis() {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [speaking, setSpeaking] = useState(false);
  const [voices, setVoices] = useState([]);
  const utteranceRef = useRef(null);

  useEffect(() => {
    if (!supported) return undefined;
    const load = () => setVoices(window.speechSynthesis.getVoices() || []);
    load();
    // Voices load asynchronously in Chrome — listen for the event
    window.speechSynthesis.addEventListener?.('voiceschanged', load);
    return () => {
      window.speechSynthesis.removeEventListener?.('voiceschanged', load);
      // Stop any active utterance on unmount
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    };
  }, [supported]);

  const pickPreferredVoice = useCallback(() => {
    if (!voices.length) return null;
    // Professor Xavier seating: STRONG male-voice preference.
    //   English (+10), localService (+3), known male name (+30),
    //   known female name (-30), male-ish keyword (+8).
    const MALE_NAMES = /\b(alex|daniel|david|mark|fred|tom|thomas|george|oliver|aaron|arthur|james|paul|jorge|diego|ryan|matthew|guy|reed|male)\b/i;
    const FEMALE_NAMES = /\b(samantha|victoria|karen|kate|susan|tessa|moira|veena|ava|allison|serena|zoe|zira|hazel|fiona|helena|female|woman)\b/i;
    const MALE_HINT = /\b(male)\b/i;
    const score = (v) => {
      let s = 0;
      if (/en[-_]/i.test(v.lang) || v.lang.toLowerCase().startsWith('en')) s += 10;
      if (v.localService) s += 3;
      if (MALE_NAMES.test(v.name)) s += 30;
      if (FEMALE_NAMES.test(v.name)) s -= 30;
      if (MALE_HINT.test(v.name)) s += 8;
      if (/natural|neural|premium|enhanced/i.test(v.name)) s += 5;
      return s;
    };
    return [...voices].sort((a, b) => score(b) - score(a))[0] || voices[0];
  }, [voices]);

  const speak = useCallback((text, opts = {}) => {
    if (!supported || !text) return;
    try {
      window.speechSynthesis.cancel(); // ensure clean start
    } catch { /* ignore */ }
    // Strip the [DIR-XXX] citation brackets from spoken output — they
    // read awkwardly aloud. The visual rendering keeps them.
    const cleaned = String(text)
      .replace(/\[DIR-\d{3}\]/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    if (!cleaned) return;

    const utter = new window.SpeechSynthesisUtterance(cleaned);
    const voice = opts.voice || pickPreferredVoice();
    if (voice) utter.voice = voice;
    utter.rate = opts.rate ?? 1.0;
    // Professor Xavier: lower pitch reinforces masculine timbre even
    // if the chosen voice engine is androgynous.
    utter.pitch = opts.pitch ?? 0.9;
    utter.volume = opts.volume ?? 1.0;
    utter.onstart = () => setSpeaking(true);
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    utteranceRef.current = utter;
    window.speechSynthesis.speak(utter);
  }, [supported, pickPreferredVoice]);

  const stop = useCallback(() => {
    if (!supported) return;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    setSpeaking(false);
  }, [supported]);

  return { supported, speaking, speak, stop, voices };
}
